import {
  AttributionKey,
  DataTableQuerySettingKey,
  DateKindKey,
  ExpressionModeKey,
  ExpressionQuerySettingKey,
  FunnelModeKey,
  FunnelOrderKey,
  FunnelQuerySettingKey,
  GroupbyKey,
  IAvailableRetentionUnit,
  ICohortCompareFieldsFragment,
  ICohortConfig,
  ICohortExecuteConfigFieldsFragment,
  ICohortFieldsFragment,
  ICohortRefPropsFieldsFragment,
  ICohortSampleOperation,
  ICohortSampleOperationFieldsFragment,
  IComparisonConfig,
  IDataTableConfigFieldsFragment,
  IDatePickerFieldsFragment,
  IDateRangeFieldsFragment,
  IEventConditionFieldsFragment,
  IExpressionCohortConfigFieldsFragment,
  IExpressionHistogramConfigFieldsFragment,
  IExpressionImpactConfigFieldsFragment,
  IExpressionQueryConfigFieldsFragment,
  IFieldFieldsFragment,
  IFilterFieldsFragment,
  IFunnelCohortConfigFieldsFragment,
  IFunnelConfigFieldsFragment,
  IFunnelModeConfigFieldsFragment,
  ILlmChatMessageFieldsFragment,
  ILogicalOperator,
  IMeasureCohortConfigFieldsFragment,
  IMeasureFieldsFragment,
  IMeasureFilterFieldsFragment,
  IMeasureFunction,
  InputMaybe,
  IPartitionValueFieldsFragment,
  IPathConfigFieldsFragment,
  IPercentileFilterInput,
  IPerformedCohortConfigFieldsFragment,
  IPredictionConfigQuery,
  IQueryConfigFieldsFragment,
  IRelationalOperator,
  IRetentionCohortConfigFieldsFragment,
  IRetentionConfigFieldsFragment,
  IRollingControl,
  ISignificanceConfigFieldsFragment,
  ISubjectFieldsFragment,
  ITimeToConvertOperatorFieldsFragment,
  IUserAliasReplacementFieldsFragment,
  Maybe,
  MeasureType,
  MetricTypeKey,
  PathDirectionKey,
  PathModeKey,
  PathQuerySettingKey,
  QueryType,
  RetentionCalculationKey,
  RetentionCalculationWindowKey,
  RetentionCohortOperatorKey,
  RetentionQuerySettingKey,
  RetentionViewKey,
  SignificanceModeKey,
  SignificanceTestKey,
  SimpleCohortTypeKey,
  TimeToConvertOperatorKey,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { MaybeNil } from '../../../utils/interfaces';
import { ITooltipOption } from '../../../components/KubitTooltip/globalTooltips';
import { IBuilderBreakdown } from './breakdown/types';
import { ISignificanceBuilderFields } from './significance/types';
import { IReplaceReport } from '../cachedEntities';
import { IBinning } from './binning/types';
import { IDateInput } from './date/types';

// https://www.typescriptlang.org/docs/handbook/enums.html
// follow the TS docs, capitalize the enum
export enum RowType {
  steps = 'steps',
  dataTableMeasures = 'dataTableMeasures',
  global = 'global',
  paths = 'paths',
  retention = 'retention',
  prediction = 'prediction',
  trigger = 'trigger',
  breakdownCohorts = 'breakdownCohorts',
  globalCohorts = 'globalCohorts',
  builderCohorts = 'builderCohorts',
  predictionCohorts = 'predictionCohorts',
  mergeEvents = 'mergeEvents',
  expandEvents = 'expandEvents',
  foldEvents = 'foldEvents',
  footer = 'footer',
  measures = 'measures',
}

export enum CohortSampleContextType {
  Percentage = 'PERCENTAGE',
  Absolute = 'ABSOLUTE',
}

export interface IBuilderSampledCohortSamplingRate {
  type: CohortSampleContextType;
  samplePercentage: number | null;
  sampleAbsolute: number | null;
}

export interface IBuilderSampledCohort {
  id: string;
  name: string;
  isCompoundCohort: boolean;
  operations: ICohortSampleOperationFieldsFragment[];
  defaultOperation: ICohortSampleOperationFieldsFragment | null;
  sampleOperation: ICohortSampleOperation | null;
  samplingRate: IBuilderSampledCohortSamplingRate;
}

export interface IQueryBuilderDefaultOptions {
  type: QueryType.Expression;
  dateKinds: IQueryConfigFieldsFragment['basicConfig']['dateKinds'];
  modes: IQueryConfigFieldsFragment['modes'];
  queryModeConfig: IExpressionQueryConfigFieldsFragment;
  impactModeConfig: IExpressionImpactConfigFieldsFragment;
  histogramModeConfig: IExpressionHistogramConfigFieldsFragment;
  samplingRate: number;
}

export interface IDataTableBuilderDefaultOptions {
  type: QueryType.DataTable;
  subjects: IDataTableConfigFieldsFragment['subjects'];
  dateKinds: IDataTableConfigFieldsFragment['basicConfig']['dateKinds'];
  samplingRate: number;
}

export interface IPathBuilderDefaultOptions {
  type: QueryType.Path;
  subjects: IPathConfigFieldsFragment['subjects'];
  partitions: IPathConfigFieldsFragment['partitions'];
  groupbys: IPathConfigFieldsFragment['groupbys'];
  partitionWindowTimeUnits: TimeUnit[];
  defaultPartitionWindowTimeUnit: TimeUnit;
  modes: IPathConfigFieldsFragment['modes'];
  samplingRate: number;
}

export interface IRetentionBuilderDefaultOptions {
  type: QueryType.Retention;
  subjects: IRetentionConfigFieldsFragment['subjects'];
  attributions: IRetentionConfigFieldsFragment['attributions'];
  groupbys: IRetentionConfigFieldsFragment['groupbys'];
  calculations: IRetentionConfigFieldsFragment['calculations'];
  calculationWindows: IRetentionConfigFieldsFragment['calculationWindows'];
  retentionViews: IRetentionConfigFieldsFragment['retentionViews'];
  defaultCohortUnits: TimeUnit[];
  defaultRetentionUnits: TimeUnit[];
  samplingRate: number;
}

export interface IFunnelBuilderDefaultOptions {
  type: QueryType.Funnel;
  subjects: IFunnelModeConfigFieldsFragment['subjects'];
  partitions: IFunnelModeConfigFieldsFragment['partitions'];
  funnelCustomOrders: IFunnelConfigFieldsFragment['customOrders'];
  funnelOrders: IFunnelModeConfigFieldsFragment['orders'];
  timeUnits: IFunnelModeConfigFieldsFragment['timeUnits'];
  dateKinds: IFunnelConfigFieldsFragment['basicConfig']['dateKinds'];
  groupbys: IFunnelConfigFieldsFragment['groupbys'];
  modes: IFunnelConfigFieldsFragment['modes'];
  partitionWindowTimeUnits: TimeUnit[];
  defaultPartitionWindowTimeUnit: TimeUnit;
  samplingRate: number;
}

export interface ISignificanceDefaultOptions {
  type: QueryType.Significance;
  subjects: ISignificanceConfigFieldsFragment['subjects'];
  modes: ISignificanceConfigFieldsFragment['modes'];
  metrics: ISignificanceConfigFieldsFragment['metrics'];
  tests: ISignificanceConfigFieldsFragment['tests'];
  experimentConfig: ISignificanceConfigFieldsFragment['experimentConfig'];
}

export type IBuilderDefaultOptions =
  | IQueryBuilderDefaultOptions
  | IDataTableBuilderDefaultOptions
  | IPathBuilderDefaultOptions
  | IRetentionBuilderDefaultOptions
  | IFunnelBuilderDefaultOptions
  | ISignificanceDefaultOptions;

export interface IQueryBuilderSelectedOptions {
  type: QueryType.Expression;
  subject: ISubjectFieldsFragment['key'];
  triggerSubject: ISubjectFieldsFragment['key'];
  dateKind: DateKindKey;
  mode: ExpressionModeKey;
  timeUnit: TimeUnit;
  includeMissingDates: boolean;
}

export interface IDataTableBuilderSelectedOptions {
  type: QueryType.DataTable;
  dateKind: DateKindKey;
}

export interface IPathBuilderSelectedOptions {
  type: QueryType.Path;
  subject: MaybeNil<ISubjectFieldsFragment['key']>;
  partitionValues: IPartitionValueFieldsFragment[];
  groupby: GroupbyKey;
  mode: PathModeKey;
}

export interface IRetentionBuilderSelectedOptions {
  type: QueryType.Retention;
  subject: MaybeNil<ISubjectFieldsFragment['key']>;
  attribution: AttributionKey;
  groupby: GroupbyKey;
  calculation: RetentionCalculationKey;
  calculationWindow: RetentionCalculationWindowKey;
  retentionView: RetentionViewKey;
  cohortUnit: TimeUnit;
}

export interface IFunnelBuilderSelectedOptions {
  type: QueryType.Funnel;
  subject: MaybeNil<ISubjectFieldsFragment['key']>;
  dateKind: DateKindKey;
  partitionValues: IPartitionValueFieldsFragment[];
  funnelOrder: FunnelOrderKey;
  funnelCustomOrder: FunnelOrderKey;
  groupby: MaybeNil<GroupbyKey>;
  mode: MaybeNil<FunnelModeKey>;
  timeUnit: TimeUnit;
}

export interface ISignificanceSelectedOptions {
  type: QueryType.Significance;
  subject: string;
  mode: SignificanceModeKey;
  metric: MetricTypeKey;
  test: SignificanceTestKey;
}

export type IBuilderSelectedOptions =
  | IQueryBuilderSelectedOptions
  | IDataTableBuilderSelectedOptions
  | IPathBuilderSelectedOptions
  | IRetentionBuilderSelectedOptions
  | IFunnelBuilderSelectedOptions
  | ISignificanceSelectedOptions;

export interface IQueryMeasureFormula {
  isValid: boolean;
  expression: string | null;
  validationError: string | null;
}

export interface IRetentionFields {
  retentionConditions: [
    IEventConditionFieldsFragment[],
    IEventConditionFieldsFragment[],
  ];
  activeRetentionUnit: TimeUnit;
  customTimeBrackets: number[];
}

export interface IBuilderLlmContext {
  // Any previous AI chat conversation messages attached to this report
  previousMessages: ILlmChatMessageFieldsFragment[];
  // is the LLM prompt visible in the UI
  isPromptVisible: boolean;
}

export interface IBuilder {
  dataTableMeasures: IRow[];
  steps: IStep[];
  stepOrders: FunnelOrderKey[];
  paths: [IEventConditionFieldsFragment[], IEventConditionFieldsFragment[]]; // start / end
  prediction: any;
  cohorts: ICohortRow[];
  logicOps: string[];
  alias?: string; // compound measure name
  showAlias?: boolean;
  selectedEvents: any;
  units: TimeUnit[];
  defaultMeasureFunctions: IMeasureFunction[];
  defaultMeasureRelOps: IRelationalOperator[];
  dateConfig: IDateConfig;
  defaultLogOps: ILogicalOperator[];
  filter?: IFilterFieldsFragment;
  global?: any;
  trigger?: IEventConditionFieldsFragment;
  errors?: IErrors;
  retention: IRetentionFields;
  direction: PathDirectionKey;
  activeSchemaId: string;
  defaultOptions: IBuilderDefaultOptions | null;
  selectedOptions: IBuilderSelectedOptions | null;
  timeWindow?: number | null;
  builderSampledCohort: IBuilderSampledCohort;
  builderCohorts: ICohortRow[];
  [RowType.globalCohorts]: ICohortRow[];
  [RowType.breakdownCohorts]: ICohortRow[];
  [RowType.predictionCohorts]: ICohortRow[];
  mergeEvents: IBuilderMergeEvent[];
  expandEvents: IBuilderExpandEvent[];
  foldEvents: IBuilderFoldEvent[];
  sourceId: MaybeNil<string>;
  compoundMeasureUserAliasId?: string;
  cachedCohorts: Record<string, ICohortFieldsFragment>;
  cohortConfig: IBuilderCohortConfig;
  formulaSamplingRate: number | null;
  /**
   * This is used when FunnelStepOrder is Custom, then user is allowed to change Seq/Exact on each row
   */
  funnelOrders?: FunnelOrderKey[];
  dashboard?: IFilterFieldsFragment;
  breakdown: IBuilderBreakdown;
  footer?: any;
  globalFrequency?: any;
  status?: any;
  timezone?: string;
  resultFilter: IMeasureFilterFieldsFragment | null;
  queryType?: QueryType;
  funnelConfig?: IFunnelConfigFieldsFragment;
  availableRetentionUnits?: IAvailableRetentionUnit[];
  defaultAdditionalSettings: IBuilderAdditionalSettings[];
  additionalSettings: IBuilderAdditionalSettingsKey[];
  compoundMeasureName: string | null;
  compoundMeasureId: string | null;
  [RowType.measures]: IRow[];
  measureFormula: IQueryMeasureFormula | null;
  comparisonConfig: Maybe<IComparisonConfig>;
  significance: ISignificanceBuilderFields;
  initialConfig: Partial<IBuilder>;
  binning: Maybe<IBinning>;
  dateInput: IDateInput;
  builderInitialized: boolean;
  llmContext: IBuilderLlmContext;
}

export interface IMeasureRow {
  type: MeasureType;
  measureId: string;
  showMeasure: boolean;
  percentile?: number;
  measureFilter: IMeasureFilterFieldsFragment | null;
  measureRollingControl?: IRollingControl;
  cohortFilter?: ICohortRow;
  isCollapsedMeasure?: boolean;
  defaultNamedMeasure: IMeasureFieldsFragment | null;
  showNamedCohort: boolean;
}

export interface IRow extends IMeasureRow {
  eventConditions: IEventConditionFieldsFragment[];
  fn: IMeasureFunction;
  userAliasId?: string;
}

export type ICohortDurationStart =
  | { required: false }
  | { required: true; date: MaybeNil<string> };

export enum LookForwardWindowRequirement {
  NotRequired = 'NOT_REQUIRED',
  Required = 'REQUIRED',
  OnRetentionOperators = 'ON_RETENTION_OPERATORS',
}

export type ILookForwardWindow =
  | { required: LookForwardWindowRequirement.NotRequired }
  | {
      required: LookForwardWindowRequirement.Required;
      windowLength: MaybeNil<number>;
      unit: TimeUnit;
    }
  | {
      required: LookForwardWindowRequirement.OnRetentionOperators;
      operators: RetentionCohortOperatorKey[];
      windowLength: MaybeNil<number>;
      unit: TimeUnit;
    };

// We are extending IRow with optional fields from it for a reason
// that cohortRow can store a measure
export interface ICohortRow extends Partial<IRow> {
  eventConditions: IEventConditionFieldsFragment[];
  fn: IMeasureFunction;
  userAliasId?: string;
  showMeasure: boolean;
  cohortId?: string;
  isCompoundCohort?: boolean;
  simpleCohortTypeKey: SimpleCohortTypeKey;
  showCohort?: boolean;
  isExpanded?: boolean;
  isActive?: boolean; // makes cohort visible

  analysisId?: string;
  measureId?: string; // used in expression cohort to indicate which measure to use
  operatorValues: Array<number | null>;

  // XXX these 3 are null for compound cohort
  subject: string | null;
  datePicker: IDatePickerFieldsFragment | null;

  timeWindow?: string;
  timeWindowValues?: Array<number | null>;
  type?: MeasureType;
  operator?: string;
  returnedOperator?: string;

  conditionFilter?: IFilterFieldsFragment | null;

  percentileFilter: InputMaybe<IPercentileFilterInput>;
  contextInput: ICohortRefPropsFieldsFragment;
  defaultNamedCohort: ICohortFieldsFragment | null;

  cohortDurationStart: ICohortDurationStart;
  lookForwardWindow: ILookForwardWindow;

  // Used in Query and Funnel cohorts.
  // If applicable, aliasReplacements will store information:
  // 1). Aliased measure names in Query
  // 2). Aliased step names in Funnel
  aliasReplacements: IUserAliasReplacementFieldsFragment[];

  timeToConvert: {
    operatorValues: Array<number | null>;
    operator: TimeToConvertOperatorKey;
    timeUnit: TimeUnit;
  } | null;
}

export interface IStep {
  name: MaybeNil<string>;
  eventConditions: IEventConditionFieldsFragment[];
  negativeSteps: IEventConditionFieldsFragment[];
  userAliasId: MaybeNil<string>;
  partitionValue: IPartitionValueFieldsFragment;
  showPartition: boolean;
}

export interface IDateConfig {
  maxDate: string;
  liveStartDate: string;
}

export interface IQueryConfigResponse {
  queryType: QueryType.Expression;
  schemaId: string;
  queryConfig: IQueryConfigFieldsFragment;
}

export interface IDataTableConfigResponse {
  queryType: QueryType.DataTable;
  schemaId: string;
  dataTableConfig: IDataTableConfigFieldsFragment;
}

export interface IFunnelConfigResponse {
  queryType: QueryType.Funnel;
  schemaId: string;
  funnelConfig: IFunnelConfigFieldsFragment;
}

export interface IPredictionConfigResponse {
  queryType: QueryType.Prediction;
  schemaId: string;
  predictionConfig: NonNullable<IPredictionConfigQuery['predictionConfig']>;
}

export interface IPathConfigResponse {
  queryType: QueryType.Path;
  schemaId: string;
  pathConfig: IPathConfigFieldsFragment;
}

export interface ICohortConfigResponse {
  queryType: QueryType.CohortSample;
  schemaId: string;
  cohortExecuteConfig: ICohortExecuteConfigFieldsFragment;
}

export interface IRetentionConfigResponse {
  queryType: QueryType.Retention;
  schemaId: string;
  retentionConfig: IRetentionConfigFieldsFragment;
}

export interface ISignificanceConfigResponse {
  queryType: QueryType.Significance;
  schemaId: string;
  significanceConfig: ISignificanceConfigFieldsFragment;
}

export interface IRowMeta {
  rowType: RowType;
  rowIndex: number;
  eventIndex?: number;
  conditionIndex?: number;
  predicateIndex?: number;
  numberIndex?: number;
  negativeStepRowIndex?: number;
  pathDirection?: string;
  isLastStep?: boolean;
  cohortRowType?: 'cohortFilter';
}

export interface IErrors {
  [RowType.dataTableMeasures]?: any;
  cohorts?: any;
  [RowType.steps]?: any;
  [RowType.global]?: any;
  [RowType.paths]?: any;
  [RowType.retention]?: any;
  [RowType.prediction]?: any;
  [RowType.trigger]?: any;
  [RowType.breakdownCohorts]?: any;
  [RowType.globalCohorts]?: any;
  [RowType.builderCohorts]?: any;
  builderSampledCohort?: any;
  [RowType.predictionCohorts]?: any;
  [RowType.mergeEvents]?: any;
  [RowType.expandEvents]?: any;
  [RowType.foldEvents]?: any;
  breakdown?: any;
  [RowType.footer]?: any;
  [RowType.measures]?: any;
  measureFormula?: any;
}

export interface IBuilderInputPayload {
  state: IBuilder;
  queryType: QueryType;
  replaceReport: IReplaceReport | null;
  analysisName?: string;
  analysisDescription?: string;
}

export interface IBuilderMergeEvent {
  isActive: boolean;
  name: string | null;
  isNameAutoGenerated: boolean;
  eventConditions: IEventConditionFieldsFragment[];
}

export interface IBuilderExpandEvent {
  isActive: boolean;
  field: MaybeNil<IFieldFieldsFragment>;
  eventCondition: IEventConditionFieldsFragment;
}

export interface IBuilderFoldEvent {
  isActive: boolean;
  eventCondition: IEventConditionFieldsFragment;
}

export type IReportFilters = Pick<
  IBuilder,
  'filter' | RowType.globalCohorts | 'breakdown' | RowType.breakdownCohorts
>;

export interface IFunnelBuilderConfig
  extends Pick<
    IBuilder,
    | 'activeSchemaId'
    | 'dateConfig'
    | 'defaultLogOps'
    | 'cohortConfig'
    | 'defaultAdditionalSettings'
    | 'dateInput'
  > {
  queryType: QueryType.Funnel;
  funnelConfig: IFunnelConfigFieldsFragment;
  defaultOptions: IFunnelBuilderDefaultOptions;
  selectedOptions: IFunnelBuilderSelectedOptions;
}

export type IPopulateFunnelBuilder = Partial<
  Pick<
    IBuilder,
    | 'steps'
    | 'stepOrders'
    | 'formulaSamplingRate'
    | 'activeSchemaId'
    | 'timeWindow'
    | 'sourceId'
    | 'defaultOptions'
    | 'additionalSettings'
    | 'dateInput'
    | 'llmContext'
  > &
    IReportFilters
> & {
  queryType: QueryType.Funnel;
  selectedOptions: Partial<IFunnelBuilderSelectedOptions>;
};

export type IFunnelBuilder = IFunnelBuilderConfig & IPopulateFunnelBuilder;

export type IPopulateQueryBuilder = Partial<
  Pick<
    IBuilder,
    | 'alias'
    | 'formulaSamplingRate'
    | 'activeSchemaId'
    | 'timeWindow'
    | 'trigger'
    | 'sourceId'
    | 'compoundMeasureUserAliasId'
    | 'resultFilter'
    | 'additionalSettings'
    | 'compoundMeasureName'
    | 'compoundMeasureId'
    | RowType.measures
    | 'measureFormula'
    | 'comparisonConfig'
    | 'dateInput'
    | 'binning'
    | 'llmContext'
  >
> & {
  queryType: QueryType.Expression;
  selectedOptions: Partial<IQueryBuilderSelectedOptions>;
} & IReportFilters;

export type IPopulatePathBuilder = Partial<
  Pick<
    IBuilder,
    | 'paths'
    | 'formulaSamplingRate'
    | 'direction'
    | 'activeSchemaId'
    | 'sourceId'
    | RowType.mergeEvents
    | RowType.expandEvents
    | RowType.foldEvents
    | 'additionalSettings'
    | 'dateInput'
    | 'llmContext'
  >
> & {
  queryType: QueryType.Path;
  selectedOptions: Partial<IPathBuilderSelectedOptions>;
} & IReportFilters;

export type IPopulateRetentionBuilder = Partial<
  Pick<
    IBuilder,
    | 'formulaSamplingRate'
    | 'retention'
    | 'activeSchemaId'
    | 'timeWindow'
    | 'sourceId'
    | 'additionalSettings'
    | 'dateInput'
    | 'llmContext'
  >
> & {
  queryType: QueryType.Retention;
  selectedOptions: Partial<IRetentionBuilderSelectedOptions>;
} & IReportFilters;

export type IPopulatePredictionBuilder = Partial<
  Pick<
    IBuilder,
    | 'prediction'
    | 'activeSchemaId'
    | 'sourceId'
    | RowType.predictionCohorts
    // RowType.globalCohorts is not a part of Prediction builder,
    // it was added to avoid extra refactoring
    | RowType.globalCohorts
    | 'dateInput'
  >
> & {
  queryType: QueryType.Prediction;
};

export type IPopulateDataTableBuilder = Partial<
  Pick<
    IBuilder,
    | 'formulaSamplingRate'
    | 'activeSchemaId'
    | 'timeWindow'
    | 'sourceId'
    | 'dataTableMeasures'
    | 'resultFilter'
    | 'additionalSettings'
    | 'comparisonConfig'
    | 'dateInput'
    | 'llmContext'
  >
> & {
  queryType: QueryType.DataTable;
  selectedOptions: Partial<IDataTableBuilderSelectedOptions>;
} & IReportFilters;

export type IPopulateSignificanceBuilder = Pick<
  IBuilder,
  | 'activeSchemaId'
  | 'significance'
  | 'filter'
  | 'sourceId'
  | RowType.globalCohorts
  | 'dateInput'
  | 'llmContext'
> & {
  queryType: QueryType.Significance;
  selectedOptions: Partial<ISignificanceSelectedOptions>;
};
export type IPopulateCohortBuilder = {
  queryType: QueryType.CohortSample;
  activeSchemaId: string;
  cohort: ICohortFieldsFragment;
  operation: ICohortSampleOperationFieldsFragment | null;
  compare: ICohortCompareFieldsFragment | null;
  samplePercentage: number | null;
  sampleAbsolute: number | null;
  dateInput: IDateInput;
  lockedDateRange: IDateRangeFieldsFragment | null;
};

export type IPopulateBuilderPayload =
  | IPopulateFunnelBuilder
  | IPopulateQueryBuilder
  | IPopulatePathBuilder
  | IPopulateRetentionBuilder
  | IPopulatePredictionBuilder
  | IPopulateDataTableBuilder
  | IPopulateSignificanceBuilder
  | IPopulateCohortBuilder;

export interface IBuilderCohortOperator {
  key: string;
  name: string;
}

type IBuilderCohortExpressionConfig = Omit<
  IExpressionCohortConfigFieldsFragment,
  'operators'
> & {
  operators: IBuilderCohortOperator[];
};

type IBuilderCohortFunnelConfig = Omit<
  IFunnelCohortConfigFieldsFragment,
  'operators'
> & {
  operators: IBuilderCohortOperator[];
  ttcConfig: {
    timeUnits: TimeUnit[];
    operators: ITimeToConvertOperatorFieldsFragment[];
  };
};

type IBuilderCohortMeasureConfig = Omit<
  IMeasureCohortConfigFieldsFragment,
  'operators'
> & {
  operators: IBuilderCohortOperator[];
};

type IBuilderCohortPerformedConfig = Omit<
  IPerformedCohortConfigFieldsFragment,
  'operators'
> & {
  operators: IBuilderCohortOperator[];
};

type IBuilderCohortRetentionConfig = Omit<
  IRetentionCohortConfigFieldsFragment,
  'operators'
> & {
  operators: IBuilderCohortOperator[];
};

export type IBuilderCohortConfig = Pick<
  ICohortConfig,
  'logicOps' | 'simpleCohortTypes' | 'defaultSamplingRate'
> & {
  expressionConfig: IBuilderCohortExpressionConfig;
  funnelConfig: IBuilderCohortFunnelConfig;
  measureConfig: IBuilderCohortMeasureConfig;
  performedConfig: IBuilderCohortPerformedConfig;
  retentionConfig: IBuilderCohortRetentionConfig;
};

export type IBuilderAdditionalSettingsKey =
  | ExpressionQuerySettingKey
  | DataTableQuerySettingKey
  | FunnelQuerySettingKey
  | PathQuerySettingKey
  | RetentionQuerySettingKey;

export interface IBuilderSettingsInterface<
  EnumType extends IBuilderAdditionalSettingsKey,
> {
  key: EnumType;
  name: string;
  tooltip?: ITooltipOption;
}

export type IQueryAdditionalSettings =
  IBuilderSettingsInterface<ExpressionQuerySettingKey>;

export type IDataTableAdditionalSettings =
  IBuilderSettingsInterface<DataTableQuerySettingKey>;

export type IFunnelAdditionalSettings =
  IBuilderSettingsInterface<FunnelQuerySettingKey>;

export type IPathAdditionalSettings =
  IBuilderSettingsInterface<PathQuerySettingKey>;

export type IRetentionAdditionalSettings =
  IBuilderSettingsInterface<RetentionQuerySettingKey>;

export type IBuilderAdditionalSettings =
  | IQueryAdditionalSettings
  | IDataTableAdditionalSettings
  | IFunnelAdditionalSettings
  | IPathAdditionalSettings
  | IRetentionAdditionalSettings;

export interface IMeasureFilterValue {
  rowMeta: IRowMeta;
  measureKey: string;
  filter: IMeasureFilterFieldsFragment | null;
}
